import React, { useMemo, useCallback, useState, useEffect, useRef } from "react";
import PropTypes from 'prop-types';
import "../../index.css";

const ChatHistory = ({
	history,
	showHistory,
	messages = [],
	npcName,
	userName,
	isTyping = false,
	onMessageSent,
	onVoiceToggle,
	isVoiceEnabled = false
}) => {
	const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(null);
	const chatContainerRef = useRef(null);
	const lastMessageRef = useRef(null);
	const messagesRef = useRef(messages);

	// Initialize component
	useEffect(() => {
		const timer = setTimeout(() => {
			setIsLoading(false);
		}, 500);

		return () => clearTimeout(timer);
	}, []);

	// Update messages ref
	useEffect(() => {
		messagesRef.current = messages;
	}, [messages]);

	// Handle data changes
	useEffect(() => {
		if (!messagesRef.current) {
			setError(new Error('No messages data available'));
			return;
		}
		setError(null);
	}, [messages]);

	// Scroll to bottom when new messages arrive
	useEffect(() => {
		if (isScrolledToBottom && chatContainerRef.current) {
			try {
				chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
			} catch (err) {
				console.error('Scroll error:', err);
			}
		}
	}, [messages, isScrolledToBottom]);

	// Handle scroll events
	const handleScroll = useCallback(() => {
		if (chatContainerRef.current) {
			try {
				const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
				const isBottom = scrollHeight - scrollTop - clientHeight < 50;
				setIsScrolledToBottom(isBottom);
			} catch (err) {
				console.error('Scroll calculation error:', err);
			}
		}
	}, []);

	// Memoize the message styles with improved visual feedback
	const getMessageStyle = useCallback((isUserMessage, isError = false) => ({
		color: "#FFFFFF",
		padding: "10px",
		marginBottom: "10px",
		display: "flex",
		alignItems: "flex-start",
		gap: "10px",
		borderRadius: "14px",
		border: `1px solid ${isError ? 'rgba(255, 0, 0, 0.3)' : 'rgba(251, 251, 251, 0.3)'}`,
		backgroundColor: isUserMessage
			? "rgba(0, 0, 0, 0.2)"
			: "rgba(110, 175, 217, 0.1)",
		backdropFilter: "blur(107px)",
		WebkitBackdropFilter: "blur(107px)",
		transition: "all 0.3s ease",
		opacity: isError ? 0.8 : 1,
		transform: isError ? 'scale(0.98)' : 'scale(1)',
	}), []);

	// Enhanced message rendering with error handling
	const renderMessage = useCallback((message, idx, messages) => {
		if (!message || (!message.content && !isTyping)) return null;

		const isUserMessage = message.sender === "user";
		const nextMessage = messages[idx + 1];
		const isNextMessageUser = !nextMessage || nextMessage.sender === "user";
		const isNextMessageNpc = !nextMessage || nextMessage.sender === "npc";
		const isError = message.error;

		if ((message.sender === "user" && !isNextMessageNpc) ||
			(message.sender === "npc" && !isNextMessageUser)) {
			return null;
		}

		const messageStyle = getMessageStyle(isUserMessage, isError);
		const icon = isUserMessage ? "/user.svg" : "/gpt.svg";
		const iconAlt = isUserMessage ? "User Icon" : "NPC Icon";
		const nameColor = isUserMessage ? "#fff" : "red";

	return (
			<section
				key={`message-${idx}`}
				ref={idx === messages.length - 1 ? lastMessageRef : null}
				className={`message-container ${isError ? 'error' : ''} ${isTyping ? 'typing' : ''}`}
			>
				<div style={messageStyle}>
					<img
						src={icon}
						alt={iconAlt}
					style={{
							width: "40px",
							height: "40px",
							borderRadius: "50%",
						}}
						onError={(e) => {
							e.target.src = isUserMessage ? "/user.svg" : "/gpt.svg";
						}}
					/>
					<div className="message-content">
						<span style={{
							color: nameColor,
							marginRight: isUserMessage ? "-5px" : "-10px",
							fontWeight: "bold",
						}}>
							{/* {isUserMessage ? userName : npcName} : */}
						</span>
						<span className={isTyping ? 'typing-indicator' : ''}>
							{message.content || (isTyping ? '...' : '')}
						</span>
						{isError && (
							<div className="error-message">
								حدث خطأ في إرسال الرسالة
							</div>
						)}
					</div>
				</div>
			</section>
		);
	}, [getMessageStyle, isTyping]);

	// Enhanced chat container with error boundary
	const chatContainer = useMemo(() => (
		<div
			ref={chatContainerRef}
					className="container-chat1"
					style={{
						maxWidth: "500px",
						height: "80%",
						overflow: "auto",
						marginBottom: "15px",
						marginTop: "15px",
						marginLeft: "20px",
						textAlign: "left",
						display: "flex",
						flexDirection: "column",
				scrollBehavior: "smooth",
			}}
			onScroll={handleScroll}
		>
			{messages.map((message, idx) => renderMessage(message, idx, messages))}
			{isTyping && renderMessage({}, messages.length, messages)}

			{!isScrolledToBottom && (
				<button
					className="scroll-to-bottom"
					onClick={() => {
						if (chatContainerRef.current) {
							chatContainerRef.current.scrollTo({
								top: chatContainerRef.current.scrollHeight,
								behavior: 'smooth'
							});
						}
					}}
													style={{
						position: 'absolute',
						bottom: '20px',
						right: '20px',
						padding: '10px',
						borderRadius: '50%',
						backgroundColor: 'rgba(0, 0, 0, 0.5)',
						border: 'none',
						cursor: 'pointer',
						color: 'white',
					}}
				>
					↓
				</button>
			)}
		</div>
	), [messages, renderMessage, isTyping, isScrolledToBottom, handleScroll]);

	// Render loading state
	const renderLoading = () => (
		<div className="chat-loading" style={{
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			height: '100%',
			color: '#fff',
			fontSize: '1.2rem'
		}}>
			Loading...
		</div>
	);

	// Render error state
	const renderError = () => (
		<div className="chat-error" style={{
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			height: '100%',
			color: '#ff4444',
			fontSize: '1.2rem',
			textAlign: 'center',
			padding: '20px'
		}}>
			<div>
				<p>Sorry, there was an error loading the conversation</p>
				<button
					onClick={() => window.location.reload()}
					style={{
						marginTop: '10px',
						padding: '8px 16px',
						backgroundColor: 'rgba(255, 68, 68, 0.1)',
						border: '1px solid #ff4444',
						borderRadius: '4px',
						color: '#ff4444',
						cursor: 'pointer'
					}}
				>
					Try Again
				</button>
			</div>
		</div>
	);

	// Main render
	return (
		<section className="chat-history-container">
			<div className={history ? "chat-History" : "chat-Historyc"}>
				<div className="chat-header">
					{/* <h3>Chat History</h3> */}
					{/* <button onClick={showHistory} className="close-button">
						<svg
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M18 6L6 18"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M6 6L18 18"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</button> */}
				</div>
				<div className="voice-control">
					<button
						onClick={onVoiceToggle}
						className={`voice-toggle ${isVoiceEnabled ? 'active' : ''}`}
													style={{
							background: 'none',
							border: 'none',
							cursor: 'pointer',
							padding: '5px',
							borderRadius: '50%',
							transition: 'all 0.3s ease',
							backgroundColor: isVoiceEnabled ? 'rgba(110, 175, 217, 0.2)' : 'transparent',
						}}
					>
						{/* {isVoiceEnabled ? '🔊' : '🔇'} */}
					</button>
													</div>
				{isLoading ? renderLoading() : error ? renderError() : chatContainer}
			</div>
		</section>
	);
};

// Enhanced PropTypes
ChatHistory.propTypes = {
	history: PropTypes.bool,
	showHistory: PropTypes.bool,
	messages: PropTypes.arrayOf(PropTypes.shape({
		sender: PropTypes.oneOf(['user', 'npc']).isRequired,
		content: PropTypes.string.isRequired,
		error: PropTypes.bool,
		timestamp: PropTypes.number
	})),
	npcName: PropTypes.string,
	userName: PropTypes.string,
	isTyping: PropTypes.bool,
	onMessageSent: PropTypes.func,
	onVoiceToggle: PropTypes.func,
	isVoiceEnabled: PropTypes.bool
};

ChatHistory.defaultProps = {
	messages: [],
	history: false,
	showHistory: false,
	isTyping: false,
	isVoiceEnabled: false
};

export default React.memo(ChatHistory);
